import { AdyenCheckout, Card } from '@adyen/adyen-web';
import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import { t } from 'i18next';

import { OrderRequests } from '~/api/Order';
import type { ApiErrorResponse } from '~/api/Requests';
import { company } from '~/company/Company';
import { cartStore, checkoutStore, mainStore } from '~/stores';
import { clearStoredPromocode, getStoredPromocode } from '~/utils/referralLink';

class AdyenService {
  private component: Card | null = null;
  private _step = 1;

  _orderId: string | null = null;

  get step() {
    return this._step;
  }

  get orderId() {
    return this._orderId;
  }

  async initPayment() {
    Sentry.withScope((scope) => {
      scope.setTag('payment_flow', 'adyen_init');
      scope.setContext('adyen_session', {
        environment: import.meta.env.REACT_APP_ENV,
      });
      Sentry.addBreadcrumb({
        category: 'payment',
        message: 'Adyen payment initialization started',
        level: 'info',
      });
    });

    checkoutStore.setIsThreeds2Active(false);
    checkoutStore.setCancellationDelay(null);
    const checkout = await AdyenCheckout({
      clientKey: company.envVariables.REACT_APP_ADYEN_CLIENT_KEY,
      environment:
        import.meta.env.REACT_APP_ENV === 'production' ? 'live' : 'test',
      showPayButton: false,
      countryCode: company.config.countryCode || 'US',

      onSubmit: async (result, component) => {
        try {
          const data = await checkoutStore.orderCheckout(null, null, {
            paymentMethodInfo: {
              paymentMethodDetails: result.data.paymentMethod || '',
              browserInfo: result?.data?.browserInfo,
              origin: (result.data as unknown as { origin: string }).origin,
            },
          });
          const orderAction =
            data &&
            data.data?.orders?.[0]?.payment.providerPaymentDetails
              ?.additionalData?.action;

          this._orderId = data?.data?.orders?.[0]?.payment.id || null;

          // Save cancellationDelay only for threeDS2
          if (orderAction?.type === 'threeDS2') {
            const cancellationDelay =
              data?.data?.orders?.[0]?.order?.cancellationDelay;
            checkoutStore.setCancellationDelay(cancellationDelay || null);
            checkoutStore.setIsThreeds2Active(true);
          } else {
            checkoutStore.setCancellationDelay(null);
            checkoutStore.setIsThreeds2Active(false);
          }

          if (!orderAction) {
            const storedPromocode = getStoredPromocode();
            await checkoutStore.finalizePayment();
            if (
              cartStore.promocode.success &&
              cartStore.promocode.value === storedPromocode?.code
            ) {
              clearStoredPromocode();
            }
            return;
          }

          component.handleAction(orderAction);
        } catch (error) {
          Sentry.withScope((scope) => {
            scope.setExtras({
              error,
            });
            Sentry.addBreadcrumb({
              category: 'payment',
              message: 'Adyen payment error',
              level: 'error',
            });
          });

          const errorInstance = error as AxiosError<ApiErrorResponse>;
          mainStore.pushAlert('error', errorInstance.message);
        }
      },
      onAdditionalDetails: (result) => {
        OrderRequests.orderPaymentDetails({
          payment_id: this._orderId || '',
          details: result.data.details,
        })
          .then(async () => {
            // Reset threeds2 state when payment is successful
            checkoutStore.setIsThreeds2Active(false);
            checkoutStore.setCancellationDelay(null);

            const storedPromocode = getStoredPromocode();
            await checkoutStore.finalizePayment();
            if (
              cartStore.promocode.success &&
              cartStore.promocode.value === storedPromocode?.code
            ) {
              clearStoredPromocode();
            }
          })
          .catch((error) => {
            const response = error.response?.data;
            let errorMessage = [error.message];
            if (response?.errors?.length) {
              const mainError = response.errors[0];
              errorMessage =
                typeof mainError === 'string'
                  ? [mainError]
                  : [mainError?.message || error.message];

              if (response?.data && response?.data?.resultCode) {
                errorMessage.push(response.data.refusalReason);
              }
            }

            mainStore.pushAlert('error', errorMessage.join('. '));
            checkoutStore.setIsLoading(false);
            // Reset threeds2 state when error occurs
            checkoutStore.setIsThreeds2Active(false);
            checkoutStore.setCancellationDelay(null);
            this.initPayment();
          });
      },
      onPaymentFailed: (result) => {
        Sentry.withScope((scope) => {
          scope.setExtras({
            result,
          });
          Sentry.addBreadcrumb({
            category: 'payment',
            message: 'Adyen payment failed',
            level: 'error',
          });
        });

        mainStore.pushAlert('error', t('errors:paymentFailed'));
        checkoutStore.setIsLoading(false);
        // Reset threeds2 state when payment fails
        checkoutStore.setIsThreeds2Active(false);
        checkoutStore.setCancellationDelay(null);

        throw new Error('[Adyen]: payment failed');
      },
      onEnterKeyPressed: () => {
        const dummy = document.createElement('input');
        document.body.appendChild(dummy);
        dummy.focus();
        dummy.blur();
        document.body.removeChild(dummy);
      },
      onError: (error) => {
        Sentry.withScope((scope) => {
          scope.setExtras({
            error,
          });
          Sentry.addBreadcrumb({
            category: 'payment',
            message: 'Adyen payment error',
            level: 'error',
          });
        });
        mainStore.pushAlert('error', error.message);
        checkoutStore.setIsLoading(false);
        throw new Error('[Adyen]: error initializing payment');
      },
    });

    Sentry.addBreadcrumb({
      category: 'payment',
      message: 'Adyen checkout configuration created',
      level: 'info',
    });

    this.component = new Card(checkout, {
      onChange: (state) => {
        checkoutStore.setIsCardValid(state.isValid);
        checkoutStore.setIsCardAdded(
          state.data && Object.keys(state.data).length > 0,
        );
      },
    }).mount('#adyen-dropin-container');

    Sentry.addBreadcrumb({
      category: 'payment',
      message: 'Adyen dropin component mounted',
      level: 'info',
    });

    checkoutStore.setIsLoading(false);
  }

  submit() {
    Sentry.addBreadcrumb({
      category: 'payment',
      message: 'Adyen payment submission triggered',
      level: 'info',
      data: {
        orderId: this._orderId,
        isValid: this.component?.isValid,
      },
    });
    this.component?.submit();
    if (this.component?.isValid) {
      checkoutStore.setIsLoading(true);
    }
  }
}

const adyenService = new AdyenService();

export default adyenService;
